import React, { useEffect, useState } from "react";
import { useSearchParams, Link, useLocation } from "react-router-dom";
import { api } from "../lib/api";

const pageWrap = {
  minHeight: "60vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "16px",
};

const card = {
  width: "100%",
  maxWidth: 960, // ~max-w-3xl
  background: "#fff",
  borderRadius: "16px",
  boxShadow: "0 8px 18px rgba(0,0,0,0.08)",
  padding: "24px",
};

const title = {
  fontSize: "20px",
  fontWeight: 600,
  color: "#111827",
  margin: "0 0 16px",
};

const text = { fontSize: 14, color: "#4b5563", margin: 0 };

const section = { border: "1px solid #e5e7eb", borderRadius: 16, padding: 16 };

const grid = {
  display: "grid",
  gap: 16,
  gridTemplateColumns: "1fr",
};
const gridMd = {
  ...grid,
  gridTemplateColumns: "2fr 1fr",
};

const codePill = {
  background: "#f3f4f6",
  borderRadius: 6,
  padding: "2px 6px",
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
};

const timeline = {
  position: "relative",
  marginLeft: 12,
  paddingLeft: 16,
  borderLeft: "1px solid #e5e7eb",
};
const tlItem = { position: "relative", margin: "0 0 16px 0", paddingLeft: 12 };
const tlDot = (active) => ({
  position: "absolute",
  left: -20,
  top: 0,
  width: 24,
  height: 24,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 12,
  color: active ? "#fff" : "#374151",
  background: active ? "#2563eb" : "#e5e7eb",
});

export function CheckoutSuccess() {
  const [params] = useSearchParams();
  const location = useLocation();
  const sessionId = params.get("session_id");
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let canceled = false;
    async function fetchSummary() {
      if (!sessionId) {
        setError("Missing session id");
        setLoading(false);
        return;
      }
      try {
        const data = await api.getJson(`/payments/session/${sessionId}/summary/`);
        if (!canceled) setSummary(data);
      } catch (e) {
        console.warn("Summary fetch failed:", e);
        if (!canceled) setError("We couldn’t load your order yet. Try refreshing.");
      } finally {
        if (!canceled) setLoading(false);
      }
    }
    fetchSummary();
    return () => {
      canceled = true;
    };
  }, [sessionId]);

  if (loading) {
    return (
      <div style={pageWrap}>
        <div style={{ ...card, maxWidth: 600 }}>
          <h1 style={title}>Finalizing your order…</h1>
          <p style={{ ...text, marginBottom: 16 }}>
            We’re processing your payment. This usually takes a few seconds.
          </p>

          <div style={timeline}>
            <div style={tlItem}>
              <div style={tlDot(true)}>1</div>
              <h3 style={{ margin: "0 0 4px", fontWeight: 600, color: "#111827" }}>
                Stripe Checkout
              </h3>
              <p style={text}>Payment submitted.</p>
            </div>
            <div style={tlItem}>
              <div style={tlDot(true)}>2</div>
              <h3 style={{ margin: "0 0 4px", fontWeight: 600, color: "#111827" }}>
                Webhook Processing
              </h3>
              <p style={text}>Recording payment & sale…</p>
            </div>
            <div style={{ ...tlItem, opacity: 0.6, marginBottom: 0 }}>
              <div style={tlDot(false)}>3</div>
              <h3 style={{ margin: "0 0 4px", fontWeight: 600, color: "#111827" }}>
                Confirmation
              </h3>
              <p style={text}>Generating order summary…</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={pageWrap}>
        <div style={{ ...card, maxWidth: 600, textAlign: "center" }}>
          <h1 style={title}>Checkout complete</h1>
          <p style={{ ...text, color: "#b91c1c" }}>{error}</p>
          <p style={{ ...text, marginTop: 8 }}>
            Session id: <code style={codePill}>{sessionId}</code>
          </p>
          <div style={{ marginTop: 16 }}>
            <Link to="/" style={{ textDecoration: "underline" }}>
              Go back home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!summary) {
    return (
      <div style={pageWrap}>
        <div style={{ ...card, maxWidth: 600, textAlign: "center" }}>
          <h1 style={title}>Thanks! Your order is being processed.</h1>
          <p style={text}>Keep this ID for reference:</p>
          <p style={{ marginTop: 8 }}>
            <code style={codePill}>{sessionId}</code>
          </p>
          <div style={{ marginTop: 16 }}>
            <Link to="/buyers/purchases" style={{ textDecoration: "underline" }}>
              View my purchases
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const { artwork, sale, payment } = summary;

  return (
    <div style={pageWrap}>
      <div style={card}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 16 }}>
          <h1 style={{ ...title, margin: 0 }}>Payment received. Thank you!</h1>
          <Link
            to="/help"
            state={{ backgroundLocation: location, helpSection: "payments-help" }}
            style={{
              width: 34,
              height: 34,
              borderRadius: "999px",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(255,255,255,0.9)",
              boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
              color: "#111827",
              textDecoration: "none",
              fontWeight: 800,
              flexShrink: 0,
            }}
            aria-label="Help for checkout success"
          >
            ?
          </Link>
        </div>
        <div style={gridMd}>
          <div style={section}>
            <h2 style={{ margin: 0, fontWeight: 600 }}>Order Summary</h2>
            <div style={{ marginTop: 12, fontSize: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0" }}>
                <span>Artwork</span>
                <span style={{ fontWeight: 600 }}>{artwork?.title ?? "—"}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0" }}>
                <span>Price</span>
                <span style={{ fontWeight: 600 }}>{sale?.price ?? artwork?.price ?? "—"}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0" }}>
                <span>Status</span>
                <span style={{ fontWeight: 600 }}>{payment?.status ?? "captured"}</span>
              </div>
              {payment?.receipt_url && (
                <div style={{ marginTop: 8 }}>
                  <a href={payment.receipt_url} target="_blank" rel="noreferrer" style={{ textDecoration: "underline" }}>
                    View Stripe receipt
                  </a>
                </div>
              )}
              {summary?.bill_of_sale?.pdf_url && (
                <div style={{ marginTop: 8 }}>
                  <a href={summary.bill_of_sale.pdf_url} target="_blank" rel="noreferrer" style={{ textDecoration: "underline" }}>
                    Download Bill of Sale
                  </a>
                </div>
              )}
            </div>
          </div>

          <div style={section}>
            <h3 style={{ margin: 0, fontWeight: 600 }}>Next steps</h3>
            <ul style={{ margin: "8px 0 0", paddingLeft: 18, fontSize: 14, color: "#374151" }}>
              <li>Your purchase has been recorded.</li>
              <li>
                View it anytime in{" "}
                <Link to="/buyers/purchases" style={{ textDecoration: "underline" }}>
                  My Purchases
                </Link>
                .
              </li>
              <li>The artist/host will be notified.</li>
            </ul>
          </div>
        </div>

        <div style={{ marginTop: 16 }}>
          <Link to="/buyers/purchases" style={{ textDecoration: "underline" }}>
            Go to My Purchases
          </Link>
        </div>
      </div>
    </div>
  );
}
